import { useState } from "react";
import { ARCHETYPE_ACCENTS, CHROME } from "./chrome";
import { PlaybookCTA } from "./PlaybookCTA";
import { ShareCard } from "./ShareCard";

const ArchetypeBlock = ({ profile, ui }) => (
  <div className="mb-6" data-testid={`pa-leader-block-${profile.id}`}>
    <div className="flex items-center gap-2 mb-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: ARCHETYPE_ACCENTS[profile.id] }} aria-hidden="true" />
      <h3 className="font-serif text-xl text-[#263b3d]">
        {profile.name} <span className="text-[#6b6b63] font-sans text-sm font-normal">— {profile.label}</span>
      </h3>
    </div>
    <p className="text-sm text-[#3f4a47] leading-relaxed mb-3">{profile.essence}</p>
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#607e66] mb-1">{ui.strength}</p>
        <p className="text-sm text-[#3f4a47] leading-relaxed">{profile.strength}</p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#b74e33] mb-1">{ui.blind}</p>
        <p className="text-sm text-[#3f4a47] leading-relaxed">{profile.blind}</p>
      </div>
    </div>
  </div>
);

const RehearsalBlock = ({ profile, ui }) => (
  <div className="mb-7 pb-7 border-b border-black/10 last:border-0 last:pb-0 last:mb-0" data-testid={`pa-exercise-block-${profile.id}`}>
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-serif text-lg text-[#263b3d]">
        {profile.name} — {ui.exerciseTitle}
      </h4>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#6b6b63] bg-black/5 px-2.5 py-1 rounded-full whitespace-nowrap">
        60s
      </span>
    </div>
    <ol className="list-decimal list-inside space-y-1.5 text-sm text-[#3f4a47] mb-3">
      {profile.steps.map((step, i) => (
        <li key={i}>{step}</li>
      ))}
    </ol>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6b6b63] mb-1">{ui.script}</p>
    <p className="text-sm italic text-[#263b3d] bg-[#f6f4ed] border-l-2 border-[#b74e33] rounded-r-md px-3 py-2 mb-3">
      &ldquo;{profile.script}&rdquo;
    </p>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6b6b63] mb-1">{ui.delivery}</p>
    <p className="text-sm text-[#3f4a47] mb-3">{profile.delivery}</p>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6b6b63] mb-1">{ui.qa}</p>
    <p className="text-sm text-[#3f4a47]">{profile.qa}</p>
  </div>
);

/**
 * Brief §8/§10 — the full on-screen Results Page: tie synthesis, every
 * leading/secondary/stretch block, the 4-count breakdown, a complete
 * rehearsal plan per co-leading archetype, direct PDF download, text
 * download, print fallback, "review my answers" / "take it again", and
 * the optional email capture. `#pa-result-printable` is exactly what the
 * browser Print / Save-as-PDF fallback captures (see
 * `presenterArchetype.css`).
 */
export const ResultScreen = ({ model, onRequestPlaybook, onDownloadPdf, onDownloadText, onPrint, onEditAnswers, onRetake, runId }) => {
  const { ui } = model;
  const c = CHROME[model.language] || CHROME.en;
  const [pdfState, setPdfState] = useState("idle"); // idle | preparing | done | error
  const [textDone, setTextDone] = useState(false);

  const heading = model.isBlend ? model.leaderProfiles.map((p) => p.name).join(" + ") : model.leaderProfiles[0].name;

  const handleDownloadPdf = async () => {
    setPdfState("preparing");
    try {
      await onDownloadPdf();
      setPdfState("done");
    } catch {
      setPdfState("error");
    }
  };

  const handleDownloadText = () => {
    onDownloadText();
    setTextDone(true);
  };

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 py-12 md:py-16 pa-root" data-testid="pa-result-screen">
      <div id="pa-result-printable">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#607e66] mb-2">
          {ui.resultEyebrow} {!model.isBlend && `— ${ui.your}`}
        </p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#263b3d] leading-tight mb-5" data-testid="pa-result-heading">
          {heading}
        </h1>

        {model.isBlend && (
          <div className="mb-7" data-testid="pa-blend-intro">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#b74e33] mb-1.5">{ui.blendTitle}</p>
            <p className="text-sm text-[#3f4a47] leading-relaxed mb-2">{model.blendText}</p>
            <p className="text-sm text-[#3f4a47] leading-relaxed">{ui.blendBody}</p>
          </div>
        )}

        {model.leaderProfiles.map((p) => (
          <ArchetypeBlock key={p.id} profile={p} ui={ui} />
        ))}

        {model.secondaryProfiles.length > 0 && (
          <div className="mb-8 pt-2" data-testid="pa-secondary-block">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6b6b63] mb-1.5">{ui.secondary}</p>
            <p className="text-sm text-[#3f4a47] leading-relaxed mb-2">{ui.secondaryBody}</p>
            {model.secondaryProfiles.map((p) => (
              <p key={p.id} className="text-sm text-[#3f4a47] mb-1">
                <strong className="text-[#263b3d]">{p.name}</strong> — {p.essence}
              </p>
            ))}
          </div>
        )}

        <div className="mb-10 pt-6 border-t border-black/10" data-testid="pa-breakdown-block">
          <h2 className="font-serif text-xl text-[#263b3d] mb-1.5">{ui.breakdown}</h2>
          <p className="text-sm text-[#3f4a47] mb-4">{ui.scoring}</p>
          <div className="space-y-3">
            {model.archetypeOrder.map((id) => {
              const count = model.scores[id];
              const name = model.allProfilesById[id].name;
              return (
                <div key={id} data-testid={`pa-score-bar-${id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-[#263b3d]">{name}</span>
                    <span className="text-xs text-[#6b6b63]">
                      {count} {count === 1 ? ui.singleAnswer : ui.answer}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-black/10">
                    <div
                      className="h-2 rounded-full"
                      style={{ width: `${(count / 7) * 100}%`, background: ARCHETYPE_ACCENTS[id] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-10 pt-6 border-t border-black/10" data-testid="pa-exercise-section">
          <h2 className="font-serif text-xl text-[#263b3d] mb-1.5">{ui.experiment}</h2>
          <p className="text-sm text-[#3f4a47] mb-6">{ui.exerciseIntro}</p>
          {model.leaderProfiles.map((p) => (
            <RehearsalBlock key={p.id} profile={p} ui={ui} />
          ))}
        </div>

        {model.stretchProfiles.length > 0 && (
          <div className="mb-10 pt-6 border-t border-black/10" data-testid="pa-stretch-block">
            <h2 className="font-serif text-xl text-[#263b3d] mb-1.5">{ui.stretchTitle}</h2>
            <p className="text-sm text-[#3f4a47] mb-3">{ui.stretchBody}</p>
            {model.stretchProfiles.map((p) => (
              <p key={p.id} className="text-sm text-[#3f4a47] mb-1">
                <strong className="text-[#263b3d]">{p.name}</strong> — {ui[`${p.id}Stretch`]}
              </p>
            ))}
          </div>
        )}

        <div className="mb-2 pt-6 border-t border-black/10" data-testid="pa-method-block">
          <h2 className="font-serif text-lg text-[#263b3d] mb-1.5">{ui.method}</h2>
          <p className="text-sm text-[#3f4a47] leading-relaxed mb-4">{ui.methodBody}</p>
          <p className="text-xs text-[#9a968a] leading-relaxed">{ui.archetypeNote}</p>
          <p className="text-xs text-[#9a968a] mt-2">{ui.footer}</p>
        </div>
      </div>

      <div className="pa-no-print mt-10 pt-8 border-t border-black/10">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6b6b63] mb-3">{ui.keep}</p>
        <div className="flex flex-wrap gap-3 mb-3">
          <button
            type="button"
            data-testid="pa-download-text-button"
            onClick={handleDownloadText}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-[#b74e33] hover:bg-[#9c3f29] transition-colors"
          >
            {ui.download}
          </button>
          <button
            type="button"
            data-testid="pa-download-pdf-button"
            onClick={handleDownloadPdf}
            disabled={pdfState === "preparing"}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-[#263b3d] border border-[#263b3d]/20 hover:border-[#263b3d]/40 disabled:opacity-50 transition-colors"
          >
            {pdfState === "preparing" ? c.pdfPreparing : model.pdfButtonLabel}
          </button>
          <button
            type="button"
            data-testid="pa-print-button"
            onClick={onPrint}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-[#263b3d] border border-[#263b3d]/20 hover:border-[#263b3d]/40 transition-colors"
          >
            {ui.print}
          </button>
        </div>
        {pdfState === "error" && (
          <p className="text-sm text-[#b74e33] mb-3" data-testid="pa-pdf-error" role="alert">
            {c.pdfError}
          </p>
        )}
        {(pdfState === "done" || textDone) && (
          <p className="text-sm text-[#607e66] mb-3" data-testid="pa-download-done">
            {ui.downloadDone}
          </p>
        )}

        <ShareCard model={model} />

        <div className="flex flex-wrap gap-4 mb-8 mt-8">
          <button
            type="button"
            data-testid="pa-edit-answers-button"
            onClick={onEditAnswers}
            className="text-sm font-medium text-[#263b3d] underline-offset-4 hover:underline"
          >
            {ui.edit}
          </button>
          <button
            type="button"
            data-testid="pa-retake-button"
            onClick={onRetake}
            className="text-sm font-medium text-[#263b3d] underline-offset-4 hover:underline"
          >
            {ui.retake}
          </button>
        </div>

        <PlaybookCTA ui={ui} onRequestPlaybook={onRequestPlaybook} runId={runId} />
      </div>
    </div>
  );
};

export default ResultScreen;
